import { useEffect, useMemo, useRef, useState } from 'react';
import { NextSearchParams } from '@/types/general';
import { IconArrowDown1, IconArrowUp1 } from '@/features/icons';
import useGetVideos from '@/app/videos/utils/use-get-videos';
import { cn } from '@/utils/cn';
import InteractifyPlayer from './player';
import { useInteractifyParams } from '../utils/use-vid-params';
import { useVideosSlider } from '../utils/use-videos-slider';
interface Props {
  closeHandler: () => void;
  searchParams?: NextSearchParams;
  fetchNextPage?: () => void;
  slug?: string;
  productSlug?: string;
  shouldLoadNextPageInSwiper?: boolean;
}
const InteractifySwiper = ({
  closeHandler,
  searchParams,
  fetchNextPage,
  slug,
  productSlug,
  shouldLoadNextPageInSwiper = true
}: Props) => {
  const {
    videos: shortsInCarousel
  } = useGetVideos({
    searchParams,
    slug,
    productSlug
  });
  const filteredSlugs = useMemo(() => shortsInCarousel.map(item => item.slug || ''), [shortsInCarousel]);
  const {
    fetchNextPage: fetchNextPageInSwiper,
    videos: videosOnlyInSwiper
  } = useGetVideos({
    enabled: false,
    filteredSlugs
  });
  const shortsList = useMemo(() => shouldLoadNextPageInSwiper ? [...shortsInCarousel, ...videosOnlyInSwiper] : shortsInCarousel, [shortsInCarousel, shouldLoadNextPageInSwiper, videosOnlyInSwiper]);
  const {
    addInteractifyParam,
    activeInteractifyParam
  } = useInteractifyParams();
  const [isVideoMuted, setIsVideoMuted] = useState(true);

  // first time index to initialize slider - no need to updated on interactify param change
  const initialIndex = useRef(shortsList?.findIndex(item => item.slug === activeInteractifyParam));
  const initialVideoParam = useRef(activeInteractifyParam);
  const shouldInsertInitialVideo = initialIndex.current === -1;
  const fetchNextPageHandler = shouldLoadNextPageInSwiper ? fetchNextPageInSwiper : fetchNextPage;
  const {
    config,
    activeIndex,
    isLoaded,
    sliderRef,
    instanceRef
  } = useVideosSlider({
    shortsList,
    fetchNextPage: fetchNextPageHandler,
    onSlideChange: (slug: string) => addInteractifyParam({
      slug
    }),
    shouldLoadNextPageInSwiper,
    initialIndex: initialIndex.current,
    initialVideoParam: initialVideoParam.current,
    shouldInsertInitialVideo
  });
  const onCommentsPanelOpen = () => {
    instanceRef.current?.update({
      ...config,
      drag: false
    });
  };
  const onCommentsPanelClose = () => {
    instanceRef.current?.update({
      ...config,
      drag: true
    });
  };
  const videoMuteStateChangeHandler = (isMuted: boolean) => {
    setIsVideoMuted(isMuted);
  };
  useEffect(() => {
    instanceRef.current?.update(config);
  }, [config, instanceRef]);
  const hasPrevSlide = activeIndex > 0;
  const hasNextSlide = activeIndex < shortsList.length - 1;
  const showArrows = isLoaded && (hasPrevSlide || hasNextSlide);
  return <div className='relative m-auto h-dvh w-screen lg:aspect-[390/820] lg:h-[90dvh] lg:w-auto lg:max-w-fit' data-sentry-component="InteractifySwiper" data-sentry-source-file="swiper.tsx">
            {showArrows && <div className='absolute -right-16 hidden h-full w-fit flex-col justify-between py-6 lg:flex'>
                    <button className={cn('rounded-md bg-surface-scrim-white p-1', {
        'cursor-default opacity-60': !hasPrevSlide
      })} onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (instanceRef.current) instanceRef.current.prev();
      }}>
                        <IconArrowUp1 className='h-8 w-8 text-white' />
                    </button>
                    <button className={cn('rounded-md bg-surface-scrim-white p-1', {
        'cursor-default opacity-60': !hasNextSlide
      })} onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (instanceRef.current) instanceRef.current.next();
      }}>
                        <IconArrowDown1 className='h-8 w-8 text-white' />
                    </button>
                </div>}

            <div ref={sliderRef} className='keen-slider h-full'>
                {shouldInsertInitialVideo && <div className='keen-slider__slide h-full !max-h-full !min-h-full'>
                        <InteractifyPlayer short={{
          slug: initialVideoParam.current
        }} isVideoMuted={isVideoMuted} closeHandler={closeHandler} videoMuteStateChangeHandler={videoMuteStateChangeHandler} onCommentsPanelOpen={onCommentsPanelOpen} onCommentsPanelClose={onCommentsPanelClose} />
                    </div>}
                {shortsList?.map((short, index) => <div key={short?.slug} className='keen-slider__slide h-full !max-h-full !min-h-full'>
                        {index >= activeIndex - 1 && index <= activeIndex + 2 && <InteractifyPlayer short={short} isVideoMuted={isVideoMuted} closeHandler={closeHandler} videoMuteStateChangeHandler={videoMuteStateChangeHandler} onCommentsPanelOpen={onCommentsPanelOpen} onCommentsPanelClose={onCommentsPanelClose} />}
                    </div>)}
            </div>
        </div>;
};
export default InteractifySwiper;